import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { setXandarKardianEnabled } from "../../api/xandarKardianService";
import XandarVitalsPanel from "./XandarVitalsPanel";
import XandarThresholdSettings from "./XandarThresholdSettings";
import XandarDeviceBindPanel from "./XandarDeviceBindPanel";

const XK_DASHBOARD_URL =
  process.env.REACT_APP_XK_DASHBOARD_URL ||
  "https://dashboard.kardian.com/login";

/**
 * GMA-786 — Xandar Kardian staff pane (toggle + portal link).
 * Mirrors PontosenseStaffSection ACL: admin manages toggle; caregivers read-only.
 */
export default function XandarKardianSection({
  clientId,
  clientName = "this client",
  clientTimezone = null,
  integrationEnabled = false,
  canManageIntegration = false,
  onIntegrationChanged,
}) {
  const [enabled, setEnabled] = useState(integrationEnabled);
  // Bumped when an admin saves in the threshold card below, so the vitals
  // panel refetches the bands it colors readings with (#581 review).
  const [thresholdsVersion, setThresholdsVersion] = useState(0);
  const [toggling, setToggling] = useState(false);
  // #583: the bind pane writes the same client as this toggle. It reports when
  // a device request is in flight so the switch can be held; the pane is held
  // in turn while `toggling`. Without that, a disable landing mid-bind 400s the
  // bind and unmounts the pane before it can report the failure.
  const [deviceBusy, setDeviceBusy] = useState(false);

  useEffect(() => {
    setEnabled(integrationEnabled);
  }, [integrationEnabled]);

  const handleToggle = async () => {
    if (toggling || deviceBusy || !clientId) return;
    const next = !enabled;
    setToggling(true);
    try {
      await setXandarKardianEnabled(clientId, next);
      setEnabled(next);
      toast.success(
        `Xandar Kardian ${next ? "enabled" : "disabled"} for ${clientName}`,
      );
      try {
        await onIntegrationChanged?.(next);
      } catch (cbErr) {
        console.warn("Xandar onIntegrationChanged callback failed", cbErr);
      }
    } catch (err) {
      toast.error(err.message || "Error updating Xandar Kardian setting");
    } finally {
      setToggling(false);
    }
  };

  const openDashboard = () => {
    window.open(XK_DASHBOARD_URL, "_blank", "noopener,noreferrer");
  };

  // A viewer who cannot manage the integration has nothing to read here while
  // it is off — no vitals, no portal link, and no action they can take. The page
  // already hides the section for exactly this user (canShowXandarStaffPane
  // gates non-admins on integrationEnabled === true), so this is unreachable
  // through the app's only mount point; it is enforced here as well so a future
  // call site that forgets the guard cannot resurrect an empty card.
  // Read the PROP, not `enabled`. The local state is synced from the prop by an
  // effect, which runs after the render commits — so a true -> false flip would
  // otherwise leave one painted render showing vitals and the portal link to a
  // viewer who is not allowed to see them. A non-manager has no toggle, so for
  // them the state can never legitimately differ from the prop anyway.
  if (!integrationEnabled && !canManageIntegration) {
    return null;
  }

  const panelStyle = {
    marginBottom: "24px",
    backgroundColor: enabled ? "#E8F5E9" : "#D9D9D9",
    color: "#333",
    padding: "16px",
    borderRadius: "8px",
    border: enabled ? "1px solid #C8E6C9" : "1px solid #BDBDBD",
    borderLeft: enabled ? "4px solid #4CAF50" : "4px solid #9E9E9E",
  };

  return (
    <div className="client-profile-section" style={panelStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <MonitorHeartIcon
            style={{ color: enabled ? "#4CAF50" : "#757575", fontSize: 28 }}
          />
          <h2 style={{ margin: 0, fontWeight: 800, fontSize: "1.1rem" }}>
            Xandar Kardian
          </h2>
        </div>
        {canManageIntegration ? (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: toggling || deviceBusy ? "not-allowed" : "pointer",
              gap: "8px",
            }}
          >
            <span style={{ fontSize: "0.9rem", color: "#555" }}>
              {enabled ? "Enabled" : "Disabled"}
            </span>
            <div
              role="switch"
              aria-checked={enabled}
              aria-disabled={toggling || deviceBusy}
              aria-label="Xandar Kardian integration"
              tabIndex={toggling || deviceBusy ? -1 : 0}
              onClick={toggling || deviceBusy ? undefined : handleToggle}
              onKeyDown={(e) => {
                if (
                  !toggling &&
                  !deviceBusy &&
                  (e.key === "Enter" || e.key === " " || e.key === "Spacebar")
                ) {
                  e.preventDefault();
                  handleToggle();
                }
              }}
              style={{
                width: "48px",
                height: "26px",
                borderRadius: "13px",
                backgroundColor: enabled ? "#4CAF50" : "#BDBDBD",
                position: "relative",
                transition: "background-color 0.3s ease",
                opacity: toggling || deviceBusy ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  position: "absolute",
                  top: "2px",
                  left: enabled ? "24px" : "2px",
                  transition: "left 0.3s ease",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
              />
            </div>
          </label>
        ) : null}
      </div>

      <p style={{ fontSize: "1rem", color: "#555", margin: "0 0 12px 0" }}>
        Contactless vital signs monitoring — heart rate, respiratory rate, and
        movement detection.
      </p>

      {enabled ? (
        <div>
          {/* #583: a sensor has to be bound to this client before any of the
              panels below can show a reading, so the bind pane comes first.
              `integrationEnabled` is `enabled`, which is already true inside
              this branch — passed anyway, on the same defence-in-depth
              reasoning as the early return above, so a future call site that
              forgets the gate cannot offer a bind the backend will 400. */}
          <XandarDeviceBindPanel
            clientId={clientId}
            canManage={canManageIntegration}
            integrationEnabled={enabled}
            integrationBusy={toggling}
            onBusyChange={setDeviceBusy}
          />

          {/* Readings land in-app, so staff can see whether the sensor is
              actually reporting without logging in to the vendor portal. */}
          <XandarVitalsPanel
            clientId={clientId}
            clientTimezone={clientTimezone}
            canManageIntegration={canManageIntegration}
            thresholdsVersion={thresholdsVersion}
          />

          {/* #581 review: the panel keeps its own copy of the bands, so a save
              here has to tell it to refetch — otherwise every displayed color
              stays on the old thresholds until a client reload. */}
          <XandarThresholdSettings
            clientId={clientId}
            canManage={canManageIntegration}
            onSaved={() => setThresholdsVersion((v) => v + 1)}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
              padding: "10px 14px",
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #C8E6C9",
            }}
          >
            <PersonSearchIcon style={{ color: "#666", fontSize: 20 }} />
            <div>
              <span
                style={{
                  fontSize: "0.85rem",
                  color: "#888",
                  display: "block",
                }}
              >
                Look up in XK portal:
              </span>
              <span style={{ fontSize: "1.05rem", fontWeight: 600 }}>
                {clientName}
              </span>
            </div>
          </div>

          <button
            onClick={openDashboard}
            className="btn-primary"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "10px 20px",
              fontSize: "1rem",
              cursor: "pointer",
            }}
          >
            <OpenInNewIcon style={{ fontSize: 18 }} />
            Open Xandar Kardian Portal
          </button>

          <p
            style={{
              fontSize: "0.85rem",
              color: "#777",
              margin: "10px 0 0 0",
              lineHeight: "1.4",
            }}
          >
            Opens the Xandar Kardian portal in a new tab. Log in with your XK
            credentials, then navigate to find this client to view their vital
            signs.
          </p>
        </div>
      ) : (
        <p style={{ fontSize: "0.9rem", color: "#888", margin: 0 }}>
          Enable this feature to allow admins and caregivers to access vital
          signs monitoring for this client via the Xandar Kardian portal.
        </p>
      )}
    </div>
  );
}
